/**
 * Release identity of THIS running bundle.
 *
 * vite.config.ts stamps every build with the git commit it was built from and
 * the build time as <meta name="sg-build-sha"> / <meta name="sg-build-time">
 * in index.html, and writes the same stamp to `version.json` beside the
 * bundle. The two together are how the app knows which version it is (Device
 * page, Settings → About, /health, the X-SG-Console-Build request header) and
 * how it notices that a newer build is live (src/pwa/update.ts compares
 * `version.json` against this).
 *
 * The stamp lives in the HTML and NOT in a JS constant on purpose: a value in
 * the module graph changes the hash of the chunk that holds it and of every
 * chunk importing it, which made each deploy re-download the whole bundle.
 * index.html is served no-cache and is tiny, so it is the right carrier.
 *
 * Without the tags (vitest, a stray index.html) the stamp reads 'dev' and the
 * update watcher treats it as "cannot be stale".
 */
export interface BuildInfo {
  /** Short git commit the bundle was built from; 'dev' when unstamped. */
  sha: string;
  /** ISO-8601 build time; '' when unstamped. */
  builtAt: string;
}

const UNSTAMPED: BuildInfo = { sha: 'dev', builtAt: '' };

function readMeta(name: string): string {
  if (typeof document === 'undefined') return '';
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')?.trim() ?? '';
}

function readBuild(): BuildInfo {
  const sha = readMeta('sg-build-sha');
  if (!sha) return UNSTAMPED;
  return { sha, builtAt: readMeta('sg-build-time') };
}

export const build: BuildInfo = readBuild();

/** True when the sha is a real commit, i.e. a comparison against it means something. */
export function isStampedBuild(info: BuildInfo = build): boolean {
  return info.sha !== 'dev' && info.sha !== 'unknown' && info.sha !== '';
}

/** "14:05" in the device's locale — for "checked at" labels. */
export function formatClock(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** "6 Oct 2026, 14:05" in the device's locale, or '—' when unstamped. */
export function formatBuiltAt(info: BuildInfo = build): string {
  if (!info.builtAt) return '—';
  const date = new Date(info.builtAt);
  if (Number.isNaN(date.getTime())) return info.builtAt;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
